import { HTTPError } from 'ky';
import { toApiError } from '@/shared/api/apiError';
import type { SupportEndReason } from './supportNotices';

const supportSessionsPath = '/api/v1/admin/support-sessions/';

/**
 * Ordnet eine gescheiterte Anfrage an die Support-Sitzung ein. Über die Sitzung sagt nur eine
 * Antwort der Support-Schnittstelle selbst etwas. `null` heißt vorübergehend — keine Antwort,
 * Überlast, Serverfehler oder eine Heimatsitzung, die sich gerade nicht erneuern ließ: die Sitzung
 * bleibt bestehen, der Aufrufer bekommt den Fehler.
 */
export async function supportEndReason(
  error: unknown,
  expiresAt?: string,
): Promise<SupportEndReason | null> {
  if (!(error instanceof HTTPError)) return null;
  if (!new URL(error.request.url).pathname.includes(supportSessionsPath)) return null;
  const { status, title } = await toApiError(error);
  // Ohne eigenen Titel lehnt dort nur die Rechteprüfung ab: diese Anmeldung führt keinen Support mehr.
  if (status === 403) return 'not-allowed';
  if (title === 'SupportSession.WorkspaceInactive') return 'workspace-locked';
  if (title !== 'SupportSession.NotActive' && title !== 'SupportSession.NotFound') return null;
  return expiresAt && Date.now() >= Date.parse(expiresAt) ? 'expired' : 'closed';
}

/**
 * Der Server hat eine Sitzung begonnen, die nicht zur Anmeldung dieses Tabs passt — meist hat
 * sich inzwischen in einem anderen Tab ein anderes Konto angemeldet. Der Tab übernimmt sie nicht.
 */
export class SupportStartMismatchError extends Error {
  readonly workspaceName: string;
  /** Bis wann sie beim Server noch laufen kann; `null`, wenn sie gleich wieder endete. */
  readonly openUntil: string | null;

  constructor(workspaceName: string, openUntil: string | null) {
    super('Die Antwort des Servers passt nicht zu dieser Anmeldung.');
    this.name = 'SupportStartMismatchError';
    this.workspaceName = workspaceName;
    this.openUntil = openUntil;
  }
}
